import React, { useState, useEffect } from 'react';
import { apiClient } from '../../utils/api';
import { AdminStats } from '../../types/admin';
import { StatsCard } from '../../components/admin/StatsCard';
import { Users, TrendingUp, Sparkles, RefreshCw } from 'lucide-react';

export const StatsTab: React.FC = () => {
  const [stats, setStats] = useState<AdminStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchStats = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiClient.get('/api/admin/stats');
      setStats(response.data);
    } catch (err: any) {
      setError(err.response?.data?.error || '获取统计数据失败');
      console.error('Failed to fetch stats:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-8 w-8 text-cyan-400 animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-900/30 border border-red-900/50 rounded-xl p-4">
        <p className="text-red-400">{error}</p>
        <button
          onClick={fetchStats}
          className="mt-2 text-red-400 hover:text-red-300 underline"
        >
          重试
        </button>
      </div>
    );
  }

  if (!stats) {
    return null;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-white">系统概览</h2>
        <button
          onClick={fetchStats}
          className="flex items-center space-x-2 px-4 py-2 bg-[#161b22] border border-cyan-900/30 rounded-md hover:bg-cyan-900/10 text-gray-300"
        >
          <RefreshCw className="h-4 w-4" />
          <span>刷新</span>
        </button>
      </div>

      {/* 用户统计 */}
      <div>
        <h3 className="text-lg font-semibold text-white mb-4">用户统计</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <StatsCard
            title="总用户数"
            value={stats.users.total}
            icon={Users}
            subtitle={`活跃用户: ${stats.users.active}`}
          />
          <StatsCard
            title="已验证用户"
            value={stats.users.verified}
            icon={Users}
            subtitle={`验证率: ${((stats.users.verified / stats.users.total) * 100).toFixed(1)}%`}
          />
          <StatsCard
            title="API Key 用户"
            value={stats.users.with_api_keys}
            icon={Users}
            subtitle={`使用率: ${((stats.users.with_api_keys / stats.users.total) * 100).toFixed(1)}%`}
          />
        </div>
      </div>

      {/* 生成统计 */}
      <div>
        <h3 className="text-lg font-semibold text-white mb-4">生成统计</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatsCard
            title="总生成数"
            value={stats.generations.total}
            icon={Sparkles}
          />
          <StatsCard
            title="图片生成"
            value={stats.generations.images}
            icon={Sparkles}
            subtitle={`${((stats.generations.images / stats.generations.total) * 100).toFixed(1)}%`}
          />
          <StatsCard
            title="音频生成"
            value={stats.generations.audio}
            icon={Sparkles}
            subtitle={`${((stats.generations.audio / stats.generations.total) * 100).toFixed(1)}%`}
          />
          <StatsCard
            title="公开内容"
            value={stats.generations.public}
            icon={Sparkles}
            subtitle={`${((stats.generations.public / stats.generations.total) * 100).toFixed(1)}%`}
          />
        </div>
      </div>

      {/* 配额统计 */}
      <div>
        <h3 className="text-lg font-semibold text-white mb-4">配额使用</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <StatsCard
            title="总配额消耗"
            value={stats.quota.total_used}
            icon={TrendingUp}
          />
          <StatsCard
            title="平均单次消耗"
            value={stats.quota.avg_cost_per_operation.toFixed(2)}
            icon={TrendingUp}
          />
          <StatsCard
            title="总操作次数"
            value={stats.quota.total_operations}
            icon={TrendingUp}
          />
        </div>
      </div>

      {/* 最近活动 */}
      <div>
        <h3 className="text-lg font-semibold text-white mb-4">
          最近 {stats.recent_activity.period_days} 天活动
        </h3>
        <div className="bg-[#161b22] rounded-xl border border-cyan-900/30 overflow-hidden">
          <table className="min-w-full divide-y divide-cyan-900/30">
            <thead className="bg-[#0d1117]">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                  日期
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                  生成数
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                  活跃用户
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-cyan-900/30">
              {stats.recent_activity.daily_stats.map((day) => (
                <tr key={day.date} className="hover:bg-cyan-900/10">
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-white">
                    {new Date(day.date).toLocaleDateString('zh-CN')}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-white">
                    {day.generations}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-white">
                    {day.active_users}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* 系统信息 */}
      <div className="bg-[#0d1117] rounded-xl border border-cyan-900/30 p-4">
        <div className="flex items-center justify-between text-sm text-gray-400">
          <span>系统版本: {stats.system.version}</span>
          <span>更新时间: {new Date(stats.system.timestamp).toLocaleString('zh-CN')}</span>
        </div>
      </div>
    </div>
  );
};
